// Call plugin.
import * as eventTypes from '../eventTypes'
import * as actionTypes from '../actionTypes'
import { OPERATIONS, OP_TRANSITIONS } from '../constants'
import { getCallById } from '../selectors'

// Handlers.
import {
  callOperationHandler,
  callEventHandler,
  stateChangeHandler,
  trackAddedHandler,
  trackRemovedHandler
} from './handlers'

/*
  "Establish" actions are:
    - MAKE_CALL
    - PENDING_MAKE_CALL
    - MAKE_CALL_FINISH
    - MAKE_ANONYMOUS_CALL_FINISH
    - CALL_ACCEPTED

    - CALL_INCOMING
    - CALL_RINGING
    - SEND_RINGING_FEEDBACK
    - SEND_RINGING_FEEDBACK_FINISH
    - SESSION_PROGRESS
    - CALL_CANCELLED

    - ANSWER_CALL
    - ANSWER_CALL_FINISH
    - REJECT_CALL
    - REJECT_CALL_FINISH
    - IGNORE_CALL
    - IGNORE_CALL_FINISH
    - FORWARD_CALL
    - FORWARD_CALL_FINISH

    - END_CALL
    - END_CALL_FINISH
 */
const callEvents = {}

callEvents[actionTypes.MAKE_CALL] = (action, params) => {
  return [
    callOperationHandler(action, {
      ...params,
      operation: OPERATIONS.MAKE,
      transition: OP_TRANSITIONS.START,
      isLocal: true
    }),
    callEventHandler(eventTypes.CALL_STARTED, action, {
      error: action.payload.error
    })
  ]
}

callEvents[actionTypes.PENDING_MAKE_CALL] = (action, params) => {
  return [
    callOperationHandler(action, {
      ...params,
      operation: OPERATIONS.MAKE,
      transition: OP_TRANSITIONS.UPDATE,
      isLocal: true
    }),
    // Notify that local tracks are available when outgoing Call is Initiated.
    trackAddedHandler(action, params),
    stateChangeHandler(action, params)
  ]
}

callEvents[actionTypes.MAKE_CALL_FINISH] = (action, params) => {
  return [
    // Event notify of "make call operation finished".
    callOperationHandler(action, {
      ...params,
      operation: OPERATIONS.MAKE,
      isLocal: true,
      transition: OP_TRANSITIONS.FINISH
    }),
    // Notify that remote tracks are available after outgoing call is Connected.
    trackAddedHandler(action, params),
    // Event to notify of Initiated-->Connected state change.
    stateChangeHandler(action, params)
  ]
}

callEvents[actionTypes.MAKE_ANONYMOUS_CALL_FINISH] = (action, context) => {
  return callEventHandler(eventTypes.CALL_STARTED, action, {
    error: action.payload.error
  })
}

callEvents[actionTypes.CALL_ACCEPTED] = (action, params) => {
  return [
    // Notify that local&remote tracks are available after a complex operation.
    trackAddedHandler(action, params),
    stateChangeHandler(action, params)
  ]
}

// ------

callEvents[actionTypes.CALL_INCOMING] = action => {
  return callEventHandler(eventTypes.CALL_INCOMING, action, {
    error: action.payload.error,
    mediaOffered: action.payload.mediaOffered
  })
}

callEvents[actionTypes.CALL_RINGING] = stateChangeHandler

callEvents[actionTypes.SEND_RINGING_FEEDBACK] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.SEND_RINGING_FEEDBACK,
    transition: OP_TRANSITIONS.START,
    isLocal: true
  })
}

callEvents[actionTypes.SEND_RINGING_FEEDBACK_FINISH] = (action, params) => {
  return [
    callOperationHandler(action, {
      ...params,
      operation: OPERATIONS.SEND_RINGING_FEEDBACK,
      isLocal: true,
      transition: OP_TRANSITIONS.FINISH
    }),
    stateChangeHandler(action, params)
  ]
}

callEvents[actionTypes.SESSION_PROGRESS] = (action, params) => {
  return [trackAddedHandler(action, params), stateChangeHandler(action, params)]
}

callEvents[actionTypes.CALL_CANCELLED] = stateChangeHandler

// ------

callEvents[actionTypes.ANSWER_CALL] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.ANSWER,
    transition: OP_TRANSITIONS.START,
    isLocal: true
  })
}

callEvents[actionTypes.ANSWER_CALL_FINISH] = (action, params) => {
  const events = [
    // Emit Call Operation Event
    callOperationHandler(action, {
      ...params,
      operation: OPERATIONS.ANSWER,
      transition: OP_TRANSITIONS.FINISH,
      isLocal: true
    }),
    // Notify that local&remote tracks are available after incoming call is Connected.
    trackAddedHandler(action, params)
  ]

  // Don't emit a stateChange event if it was a slow start answer. The call isn't actually
  //    answered yet, so state hasn't changed.
  // TODO: Don't have this in meta?
  if (action.meta && action.meta.isSlowStart) {
    return events
  }

  events.push(stateChangeHandler(action, params))
  return events
}

callEvents[actionTypes.REJECT_CALL] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.REJECT,
    transition: OP_TRANSITIONS.START,
    isLocal: true
  })
}

callEvents[actionTypes.REJECT_CALL_FINISH] = (action, params) => {
  return [
    callOperationHandler(action, {
      ...params,
      operation: OPERATIONS.REJECT,
      isLocal: true,
      transition: OP_TRANSITIONS.FINISH
    }),
    stateChangeHandler(action, params)
  ]
}

callEvents[actionTypes.IGNORE_CALL] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.IGNORE,
    transition: OP_TRANSITIONS.START,
    isLocal: true
  })
}

callEvents[actionTypes.IGNORE_CALL_FINISH] = (action, params) => {
  return [
    callOperationHandler(action, {
      ...params,
      operation: OPERATIONS.IGNORE,
      isLocal: true,
      transition: OP_TRANSITIONS.FINISH
    }),
    stateChangeHandler(action, params)
  ]
}

callEvents[actionTypes.FORWARD_CALL] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.FORWARD_CALL,
    transition: OP_TRANSITIONS.START,
    isLocal: true
  })
}

callEvents[actionTypes.FORWARD_CALL_FINISH] = (action, params) => {
  return [
    callOperationHandler(action, {
      ...params,
      operation: OPERATIONS.FORWARD_CALL,
      isLocal: true,
      transition: OP_TRANSITIONS.FINISH
    }),
    stateChangeHandler(action, params)
  ]
}

// ------

callEvents[actionTypes.END_CALL] = (action, params) => {
  return callOperationHandler(action, {
    ...params,
    operation: OPERATIONS.END,
    transition: OP_TRANSITIONS.START,
    isLocal: true
  })
}

callEvents[actionTypes.END_CALL_FINISH] = (action, params) => {
  // When the call is ending, for any reason, get all tracks previously in state
  //    for the "tracks removed" event.
  const call = getCallById(params.prevState, action.payload.id) || {}
  const { id, localTracks, remoteTracks } = call
  const payload = { localTracks, remoteTracks, id }

  return [
    callOperationHandler(action, {
      ...params,
      operation: OPERATIONS.END,
      transition: OP_TRANSITIONS.FINISH,
      isLocal: action.payload.isLocal
    }),
    // Notify that local&remote tracks are not available when Call ends.
    trackRemovedHandler({ payload }),
    stateChangeHandler(action, params)
  ]
}

export default callEvents
